import { memo } from 'react';
import { ListChecks } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useMemberPreference } from '@/hooks/useMemberPreference';

/**
 * 마이페이지 "기능 설정" 섹션 — 셀러별 opt-in 기능 토글의 집.
 * 결제수단 기록(현금/카드): 기본 OFF.
 * 읽기 실패 시 OFF로 렌더하지 않는다(체크박스 비활성 + 에러 문구) — 진짜 설정 덮어쓰기 방지.
 */
export const FeatureSettingsSection = memo(function FeatureSettingsSection() {
  const { user } = useAuth();
  const { recordPaymentMethod, preferenceStatus, setRecordPaymentMethod } = useMemberPreference({ user });

  if (!user) return null;

  const handleToggle = async (checked: boolean) => {
    const ok = await setRecordPaymentMethod(checked);
    if (ok) {
      toast.success(checked ? '결제수단 기록이 켜졌습니다.' : '결제수단 기록이 꺼졌습니다.');
    } else {
      toast.error('설정 저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
      <div className="border-b px-6 py-4 flex items-center">
        <ListChecks className="h-5 w-5 mr-2 text-teal-500" />
        <h2 className="text-xl font-semibold text-gray-900">기능 설정</h2>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-800">결제수단 기록 (현금/카드)</h3>
            <p className="text-sm text-gray-500 mt-1">
              주문에 현금/카드 결제수단을 기재하고, 오늘얼마에서 월별 분리 집계를 확인할 수 있어요
              (세금 신고 전 월별 확인용)
            </p>
            {preferenceStatus === 'error' && (
              <p className="text-sm text-red-500 mt-1">
                설정을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.
              </p>
            )}
          </div>
          <Checkbox
            className="mt-1"
            checked={recordPaymentMethod}
            onCheckedChange={(checked) => handleToggle(checked === true)}
            disabled={preferenceStatus !== 'ready'}
            aria-label="결제수단 기록 켜기/끄기"
          />
        </div>
      </div>
    </div>
  );
});
